import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ReconBillUploadTable, {
  ReconBillUploadRow,
} from "../../../components/patient/BillAuditStage/ReconBillUploadTable";
import {
  cancelReconBill,
  linkReconBillDocument,
  unlinkReconBillDocument,
  DsAiRead,
} from "../../../apis/reconBillUploadService";
import { fetchBillHeaderOcr } from "../../../apis/ocrBillHeaderService";
import { deleteUploadedDocument } from "../../../apis/patientService";
import { UploadDocument } from "../../../models/EnhancementSlice";
import { AppDispatch, RootState } from "../../../store/store";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";
import { isJfifFile, rejectOversizedHospitalBillFile } from "../../../utils/fileUploadValidation";
import {
  DS_ROW_KEY,
  OTHERS_ROW_KEY,
  getDischargeSummaryDocumentType,
  getHospitalBillDocumentType,
  getOthersDocumentType,
  isIpOrEmergencyVisit,
  isSyntheticReconRow,
} from "../../../utils/reconBillUploadUtils";
import {
  applyOcrHeadersToReconRows,
  OcrByReconId,
  resolveBillUrlForOcr,
} from "../../../utils/reconBillOcrUtils";
import { hasAnyReconBillMismatch } from "../../../utils/reconBillMismatchUtils";
import FilePreviewModal from "../../../components/patient/PreAuth-query/FilePreviewModal";
import SelectedFilePreviewModal from "../../../components/patient/PreAuth-query/SelectedFilePreviewModal";
import ConfirmSubmissionModalStage from "../../../components/modals/ConfirmSubmissionModalStage";

const { Title, Text } = Typography;
const { TextArea } = Input;

type UploadKind = "BILL" | "DS" | "OTHERS";

type ReconBillUploadContainerProps = {
  rows: ReconBillUploadRow[];
  dsRead?: DsAiRead | null;
  loading: boolean;
  refetch: (options?: { silent?: boolean }) => Promise<void>;
  hospitalId?: number;
  uploadDisabled?: boolean;
  /** OTHERS stays enabled until Audited / Summary Generated */
  othersUploadDisabled?: boolean;
  /** After Send to QC / Bill Uploaded — gray columns come from aiReader only */
  isBillAuditIntimated?: boolean;
  documentTypes: any[];
  documentUrls: any[];
  handleOpen: (docType: any, documentUrl: any) => void;
  onDocumentAttached: (uploadedDocs: any[]) => Promise<void>;
  onDocumentDeleted: (documentUrl: string) => void;
  schemeMetaDataName?: string;
  visitType?: string;
  onHasMismatchChange?: (hasMismatch: boolean) => void;
};

const ReconBillUploadContainer: FC<ReconBillUploadContainerProps> = ({
  rows,
  dsRead = null,
  loading,
  refetch,
  hospitalId,
  uploadDisabled = false,
  othersUploadDisabled = false,
  isBillAuditIntimated = false,
  documentTypes,
  documentUrls,
  handleOpen,
  onDocumentAttached,
  onDocumentDeleted,
  schemeMetaDataName,
  visitType,
  onHasMismatchChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { patients, auth, enhancement } = useSelector(
    (state: RootState) => state,
  );

  const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
  const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
  const expectedPatientName =
    patients?.fetchBasicDetails?.patientResponse?.fullName;
  const expectedUhid = patients?.fetchBasicDetails?.patientResponse?.uhidNo;
  const isIpEmergency = isIpOrEmergencyVisit(visitType);
  /** Pre–Send to QC: fill gray columns from OCR. After intimate → aiReader only. */
  const useOcrForGrayColumns = !isBillAuditIntimated;

  const [uploadingReconIds, setUploadingReconIds] = useState<number[]>([]);
  const [deletingReconIds, setDeletingReconIds] = useState<number[]>([]);
  const [cancellingReconIds, setCancellingReconIds] = useState<number[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    documentType: "",
  });
  const [pendingReconIds, setPendingReconIds] = useState<number[]>([]);
  const [pendingUploadKind, setPendingUploadKind] =
    useState<UploadKind>("BILL");
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [pendingCancelRecord, setPendingCancelRecord] =
    useState<ReconBillUploadRow | null>(null);
  const [cancelledReason, setCancelledReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [ocrByReconId, setOcrByReconId] = useState<OcrByReconId>({});
  const ocrRequestedRef = useRef<Set<string>>(new Set());
  const ocrByReconIdRef = useRef<OcrByReconId>({});
  ocrByReconIdRef.current = ocrByReconId;

  const hospitalBillDocumentType = useMemo(
    () => getHospitalBillDocumentType(documentTypes),
    [documentTypes],
  );
  const dischargeSummaryDocumentType = useMemo(
    () => getDischargeSummaryDocumentType(documentTypes),
    [documentTypes],
  );
  const othersDocumentType = useMemo(
    () => getOthersDocumentType(documentTypes),
    [documentTypes],
  );

  const isEchsOrCghsNew =
    schemeMetaDataName?.toUpperCase() === "ECHS" ||
    schemeMetaDataName?.toUpperCase() === "CGHS NEW";

  const runBillHeaderOcr = useCallback(
    async (
      documentId: string,
      documentUrl: string | null | undefined,
      targetReconIds: number[],
    ) => {
      if (!useOcrForGrayColumns || !documentId || !targetReconIds?.length) {
        return;
      }
      const billUrl = resolveBillUrlForOcr(documentUrl);
      if (!billUrl) {
        return;
      }

      const reconKeys = targetReconIds.map(String);
      setOcrByReconId((prev) => {
        const next = { ...prev };
        reconKeys.forEach((key) => {
          next[key] = {
            loading: true,
            billHeaders: prev[key]?.billHeaders || [],
          };
        });
        return next;
      });

      try {
        const response = await fetchBillHeaderOcr(billUrl);
        const billHeaders = Array.isArray(response?.billHeaders)
          ? response.billHeaders
          : Array.isArray((response as any)?.data?.billHeaders)
            ? (response as any).data.billHeaders
            : [];
        setOcrByReconId((prev) => {
          const next = { ...prev };
          reconKeys.forEach((key) => {
            next[key] = { loading: false, billHeaders };
          });
          return next;
        });
      } catch (err) {
        console.error("Failed to fetch bill header OCR", err);
        setOcrByReconId((prev) => {
          const next = { ...prev };
          reconKeys.forEach((key) => {
            next[key] = {
              loading: false,
              billHeaders: prev[key]?.billHeaders || [],
              error: "OCR failed",
            };
          });
          return next;
        });
        errorMessage("Failed to read bill header from uploaded document");
      }
    },
    [useOcrForGrayColumns],
  );

  // Clear OCR cache once Send to QC / intimated — gray columns use aiReader from list.
  useEffect(() => {
    if (!useOcrForGrayColumns) {
      setOcrByReconId({});
      ocrRequestedRef.current.clear();
    }
  }, [useOcrForGrayColumns]);

  // After list refresh: OCR only rows that still have no gray data and were not already read.
  useEffect(() => {
    if (!useOcrForGrayColumns || !rows?.length) {
      return;
    }
    rows.forEach((row) => {
      if (row.cancelled || isSyntheticReconRow(row) || !row.documentId) {
        return;
      }
      const reconKey = String(row.reconId);
      if (ocrRequestedRef.current.has(reconKey)) {
        return;
      }
      const existingCache = ocrByReconIdRef.current[reconKey];
      if (existingCache?.billHeaders?.length || existingCache?.loading) {
        ocrRequestedRef.current.add(reconKey);
        return;
      }
      if (row.aiPatientName || row.aiUhid || row.aiBillNo || row.aiDoa) {
        ocrRequestedRef.current.add(reconKey);
        return;
      }
      const documentId = String(row.documentId);
      const matchedDocument = documentUrls?.find(
        (doc) => String(doc?.id) === documentId,
      );
      const documentUrl = matchedDocument?.documentUrl || row.documentUrl;
      if (!documentUrl) {
        return;
      }
      ocrRequestedRef.current.add(reconKey);
      runBillHeaderOcr(documentId, documentUrl, [row.reconId]);
    });
  }, [useOcrForGrayColumns, rows, documentUrls, runBillHeaderOcr]);

  const tableRows = useMemo(() => {
    let billRows = rows.map((row) => {
      const matchedDocument = documentUrls?.find(
        (doc) => String(doc?.id) === String(row.documentId),
      );
      return {
        ...row,
        documentKind: row.documentKind || "BILL",
        documentUrl: matchedDocument?.documentUrl,
        documentLabelType:
          matchedDocument?.documentLabelType || hospitalBillDocumentType?.label,
        documentName:
          matchedDocument?.documentName ||
          matchedDocument?.fileName ||
          matchedDocument?.name,
      } as ReconBillUploadRow;
    });

    // Pre–Send to QC: overlay OCR headers onto gray columns by billNo match.
    if (useOcrForGrayColumns) {
      billRows = applyOcrHeadersToReconRows(billRows, ocrByReconId);
    }

    if (billRows.length === 0) {
      return billRows;
    }

    const result = [...billRows];
    const seed = billRows.find((row) => !row.cancelled) || billRows[0];

    if (isIpEmergency) {
      const dsDoc = documentUrls?.find(
        (doc) =>
          doc?.documentLabelType?.toUpperCase() === "DISCHARGE SUMMARY",
      );
      result.push({
        key: DS_ROW_KEY,
        reconId: DS_ROW_KEY,
        documentKind: "DS",
        ipNumber: seed?.ipNumber,
        doa: seed?.doa,
        dod: seed?.dod,
        aiPatientName: dsRead?.patientName,
        aiUhid: dsRead?.uhid,
        aiDoa: dsRead?.doa,
        aiDod: dsRead?.dod,
        aiReadStatus: dsRead?.aiReadStatus,
        documentId: dsDoc?.id != null ? String(dsDoc.id) : undefined,
        documentUrl: dsDoc?.documentUrl,
        documentLabelType: dsDoc?.documentLabelType || "DISCHARGE SUMMARY",
        documentName:
          dsDoc?.documentName || dsDoc?.fileName || dsDoc?.name || "DS.PDF",
      });
    }

    const othersDoc = documentUrls?.find(
      (doc) => doc?.documentLabelType?.toUpperCase() === "OTHERS",
    );
    result.push({
      key: OTHERS_ROW_KEY,
      reconId: OTHERS_ROW_KEY,
      documentKind: "OTHERS",
      ipNumber: seed?.ipNumber,
      documentId: othersDoc?.id != null ? String(othersDoc.id) : undefined,
      documentUrl: othersDoc?.documentUrl,
      documentLabelType: othersDoc?.documentLabelType || "OTHERS",
      documentName:
        othersDoc?.documentName ||
        othersDoc?.fileName ||
        othersDoc?.name ||
        "OTHERS.PDF",
    });

    return result;
  }, [
    rows,
    documentUrls,
    hospitalBillDocumentType?.label,
    isIpEmergency,
    dsRead,
    useOcrForGrayColumns,
    ocrByReconId,
  ]);

  useEffect(() => {
    onHasMismatchChange?.(
      hasAnyReconBillMismatch(
        tableRows,
        expectedUhid,
        expectedPatientName,
      ),
    );
  }, [
    tableRows,
    expectedUhid,
    expectedPatientName,
    onHasMismatchChange,
  ]);

  const resolveDocumentType = (uploadKind: UploadKind) => {
    if (uploadKind === "DS") {
      return dischargeSummaryDocumentType;
    }
    if (uploadKind === "OTHERS") {
      return othersDocumentType;
    }
    return hospitalBillDocumentType;
  };

  const resolveUploadKindLabel = (uploadKind: UploadKind) => {
    if (uploadKind === "DS") {
      return "Discharge Summary";
    }
    if (uploadKind === "OTHERS") {
      return "Others";
    }
    return "Hospital Bill";
  };

  const closePreviewModal = () => {
    if (previewFile.url) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewModalVisible(false);
    setSelectedFile(null);
    setPreviewFile({ url: "", name: "", documentType: "" });
    setPendingReconIds([]);
    setPendingUploadKind("BILL");
  };

  const closeCancelModal = () => {
    if (isCancelling) {
      return;
    }
    setCancelModalVisible(false);
    setPendingCancelRecord(null);
    setCancelledReason("");
  };

  const handleFileSelect = (
    file: File,
    reconIds: number[],
    uploadKind: UploadKind = "BILL",
  ) => {
    const documentType = resolveDocumentType(uploadKind);
    if (!documentType?.labelId) {
      errorMessage(
        `${resolveUploadKindLabel(uploadKind)} document type not configured`,
      );
      return;
    }
    if (
      schemeMetaDataName?.toUpperCase() === "CGHS NEW" &&
      isJfifFile(file)
    ) {
      errorMessage(
        "JFIF files are not allowed. Please upload a valid PDF, JPG, JPEG, or PNG file.",
      );
      return;
    }
    if (
      schemeMetaDataName?.toUpperCase() === "CGHS NEW" &&
      uploadKind === "BILL" &&
      rejectOversizedHospitalBillFile(file)
    ) {
      return;
    }
    if (isEchsOrCghsNew && file.type !== "application/pdf") {
      errorMessage("File Must be PDF only");
      return;
    }

    const uniqueReconIds = Array.from(
      new Set((reconIds || []).map(Number).filter((id) => !Number.isNaN(id))),
    );
    if (uniqueReconIds.length === 0) {
      errorMessage("Select at least one row to upload");
      return;
    }

    setPendingReconIds(uniqueReconIds);
    setPendingUploadKind(uploadKind);
    setSelectedFile(file);
    setPreviewFile({
      url: URL.createObjectURL(file),
      name: file.name,
      documentType: String(documentType.labelId),
    });
    setPreviewModalVisible(true);
  };

  const handleUpload = async (
    file: File,
    reconIds: number[],
    uploadKind: UploadKind = "BILL",
  ) => {
    if (!hospitalId || !caseId || !visitId) {
      errorMessage("Missing case or visit details");
      return;
    }
    const documentType = resolveDocumentType(uploadKind);
    if (!documentType?.labelId) {
      errorMessage(
        `${resolveUploadKindLabel(uploadKind)} document type not configured`,
      );
      return;
    }

    const targetReconIds = Array.from(
      new Set((reconIds || []).map(Number).filter((id) => !Number.isNaN(id))),
    );
    if (targetReconIds.length === 0) {
      errorMessage("No selected rows to upload");
      return;
    }

    closePreviewModal();
    setUploadingReconIds(targetReconIds);
    try {
      const formData = new FormData();
      formData.append("hospitalId", hospitalId.toString());
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        formData.append(
          "patientId",
          patients.fetchBasicDetails.patientResponse.id.toString(),
        );
      }
      formData.append("caseId", caseId.toString());
      formData.append("visitId", visitId.toString());
      formData.append("documentType", String(documentType.labelId));
      formData.append("file", file);

      const documentRes = await dispatch(
        UploadDocument({
          formData,
          accessToken: auth.accessToken || "",
        }),
      ).unwrap();

      const uploadedDocs = Array.isArray(documentRes)
        ? documentRes
        : [documentRes];
      const uploadedDoc = uploadedDocs[0];
      const documentId = uploadedDoc?.id?.toString();
      if (!documentId) {
        throw new Error("Document id missing after upload");
      }

      await onDocumentAttached(uploadedDocs);

      if (uploadKind === "BILL") {
        const billReconIds = targetReconIds.filter(
          (id) => id !== DS_ROW_KEY && id !== OTHERS_ROW_KEY,
        );
        if (billReconIds.length > 0) {
          await linkReconBillDocument({
            hospitalId,
            documentId,
            reconIds: billReconIds,
          });
        }
        successMessage(
          `${uploadedDoc?.documentLabelType || documentType.label} uploaded successfully`,
        );
        setSelectedRowKeys([]);
        const ocrUrl =
          uploadedDoc?.documentUrl ||
          uploadedDoc?.documentPath ||
          uploadedDoc?.url;
        if (useOcrForGrayColumns && billReconIds.length > 0 && ocrUrl) {
          billReconIds.forEach((id) =>
            ocrRequestedRef.current.add(String(id)),
          );
        }
        await refetch({ silent: true });
        if (useOcrForGrayColumns && billReconIds.length > 0 && ocrUrl) {
          await runBillHeaderOcr(documentId, ocrUrl, billReconIds);
        }
      } else {
        successMessage(
          `${uploadedDoc?.documentLabelType || documentType.label} uploaded successfully`,
        );
        setSelectedRowKeys([]);
      }
    } catch (err) {
      console.error("Failed to upload and link bill document", err);
      errorMessage("Failed to upload bill document");
    } finally {
      setUploadingReconIds([]);
    }
  };

  const handleConfirmUpload = async (file?: File) => {
    const uploadFile = file || selectedFile;
    if (!uploadFile || pendingReconIds.length === 0) {
      return;
    }
    await handleUpload(uploadFile, pendingReconIds, pendingUploadKind);
  };

  const handlePreview = (record: ReconBillUploadRow) => {
    if (!record.documentUrl) {
      errorMessage("Document preview not available");
      return;
    }
    const fallbackLabel =
      record.documentKind === "DS"
        ? "DISCHARGE SUMMARY"
        : record.documentKind === "OTHERS"
          ? "OTHERS"
          : "HOSPITAL BILL";
    handleOpen(record.documentLabelType || fallbackLabel, record.documentUrl);
  };

  const handleDelete = async (record: ReconBillUploadRow) => {
    if (!hospitalId) {
      errorMessage("Missing hospital details");
      return;
    }

    const isDs = record.documentKind === "DS" || record.reconId === DS_ROW_KEY;
    const isOthers =
      record.documentKind === "OTHERS" || record.reconId === OTHERS_ROW_KEY;
    const matchedDocument = isDs
      ? documentUrls?.find(
          (doc) =>
            doc?.documentLabelType?.toUpperCase() === "DISCHARGE SUMMARY",
        )
      : isOthers
        ? documentUrls?.find(
            (doc) => doc?.documentLabelType?.toUpperCase() === "OTHERS",
          )
        : documentUrls?.find(
            (doc) => String(doc?.id) === String(record.documentId),
          );

    if (!matchedDocument?.documentUrl || !matchedDocument?.id) {
      errorMessage("Document details not found");
      return;
    }

    const linkedReconIds = isDs
      ? [DS_ROW_KEY]
      : isOthers
        ? [OTHERS_ROW_KEY]
        : tableRows
            .filter(
              (row) =>
                !isSyntheticReconRow(row) &&
                String(row.documentId) === String(record.documentId),
            )
            .map((row) => row.reconId);

    setDeletingReconIds(linkedReconIds);
    try {
      await deleteUploadedDocument({
        documentUrl: matchedDocument.documentUrl,
        status: false,
        id: matchedDocument.id,
      });
      if (!isDs && !isOthers) {
        await unlinkReconBillDocument({
          hospitalId,
          reconIds: linkedReconIds,
        });
      }
      onDocumentDeleted(matchedDocument.documentUrl);
      setSelectedRowKeys((prev) =>
        prev.filter((reconId) => !linkedReconIds.includes(reconId)),
      );
      if (!isDs && !isOthers) {
        linkedReconIds.forEach((id) =>
          ocrRequestedRef.current.delete(String(id)),
        );
        setOcrByReconId((prev) => {
          const next = { ...prev };
          let changed = false;
          linkedReconIds.forEach((id) => {
            const key = String(id);
            if (next[key]) {
              delete next[key];
              changed = true;
            }
          });
          return changed ? next : prev;
        });
        await refetch();
      }
    } catch (err) {
      console.error("Failed to delete bill document", err);
    } finally {
      setDeletingReconIds([]);
    }
  };

  const handleCancelBillClick = (record: ReconBillUploadRow) => {
    if (isSyntheticReconRow(record)) {
      return;
    }
    setPendingCancelRecord(record);
    setCancelledReason("");
    setCancelModalVisible(true);
  };

  const handleConfirmCancelBill = async () => {
    if (!hospitalId || !pendingCancelRecord?.reconId) {
      errorMessage("Missing hospital or bill details");
      return;
    }
    const reason = cancelledReason.trim();
    if (!reason) {
      errorMessage("Please enter cancellation reason");
      return;
    }

    const reconId = pendingCancelRecord.reconId;
    setIsCancelling(true);
    setCancellingReconIds([reconId]);
    try {
      await cancelReconBill({
        hospitalId,
        reconId,
        cancelledReason: reason,
      });
      successMessage("Bill cancelled successfully");
      setSelectedRowKeys((prev) => prev.filter((id) => id !== reconId));
      setCancelModalVisible(false);
      setPendingCancelRecord(null);
      setCancelledReason("");
      await refetch();
    } catch (err) {
      console.error("Failed to cancel bill", err);
      errorMessage("Failed to cancel bill");
    } finally {
      setIsCancelling(false);
      setCancellingReconIds([]);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0, color: "#004c8f" }}>
          BILL UPLOAD
        </Title>
      </div>
      <ReconBillUploadTable
        rows={tableRows}
        loading={
          loading &&
          uploadingReconIds.length === 0 &&
          deletingReconIds.length === 0
        }
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        onUpload={(file, reconIds) => {
          const uploadKind: UploadKind = reconIds.includes(OTHERS_ROW_KEY)
            ? "OTHERS"
            : reconIds.includes(DS_ROW_KEY)
              ? "DS"
              : "BILL";
          handleFileSelect(file, reconIds, uploadKind);
        }}
        uploadingReconIds={uploadingReconIds}
        deletingReconIds={deletingReconIds}
        cancellingReconIds={cancellingReconIds}
        pendingCancelReconId={pendingCancelRecord?.reconId ?? null}
        uploadDisabled={uploadDisabled}
        othersUploadDisabled={othersUploadDisabled}
        onPreview={handlePreview}
        onDelete={handleDelete}
        onCancelBill={handleCancelBillClick}
        expectedPatientName={expectedPatientName}
        expectedUhid={expectedUhid}
        visitType={visitType}
      />

      {previewModalVisible &&
        selectedFile &&
        (isEchsOrCghsNew ? (
          <FilePreviewModal
            isVisible={previewModalVisible}
            onClose={closePreviewModal}
            fileUrl={previewFile.url}
            fileName={previewFile.name}
            handleUpload={handleConfirmUpload}
            showUploadButton={true}
            documentType={previewFile.documentType}
            isLoading={enhancement?.isLoading}
          />
        ) : (
          <SelectedFilePreviewModal
            isVisible={previewModalVisible}
            onClose={closePreviewModal}
            fileUrl={previewFile.url}
            fileName={previewFile.name}
            documentType={previewFile.documentType}
            handleUpload={handleConfirmUpload}
            showUploadButton={true}
            key={selectedFile.lastModified}
            isLoading={enhancement?.isLoading}
            handleUploadFile={handleConfirmUpload}
            isHospitalBill={pendingUploadKind === "BILL"}
          />
        ))}

      <ConfirmSubmissionModalStage
        open={cancelModalVisible}
        title={`Do you want to cancel bill${
          pendingCancelRecord?.billNo ? ` ${pendingCancelRecord.billNo}` : ""
        }?`}
        handleCancel={closeCancelModal}
        handleSave={handleConfirmCancelBill}
        isLoading={isCancelling}
        width={520}
        content={
          <div style={{ textAlign: "left" }}>
            <Text strong>
              Cancellation Reason <span style={{ color: "red" }}>*</span>
            </Text>
            <TextArea
              value={cancelledReason}
              onChange={(e) => setCancelledReason(e.target.value)}
              rows={4}
              placeholder="Enter reason for cancellation"
              style={{ marginTop: 8 }}
              disabled={isCancelling}
            />
          </div>
        }
      />
    </div>
  );
};

export default ReconBillUploadContainer;
